import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, MessageSquare, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function WhatsAppTemplatesPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/whatsapp/templates").then((r) => setData(r.data)); }, []);
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("تم النسخ"); }
    catch { toast.error("تعذر النسخ"); }
  };
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="wa-templates-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-black text-slate-900">قوالب واتساب المعتمدة</h1>
        <p className="text-slate-500 mt-2">
          هذه القوالب تُقدَّم لـ Meta عبر Twilio للحصول على الاعتماد، وتسمح بمراسلة أولياء الأمور حتى خارج نافذة ٢٤ ساعة
        </p>
      </div>

      <Card className={`border ${data.twilio_configured ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}>
        <CardContent className="p-5 flex items-start gap-3">
          {data.twilio_configured ? <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0 mt-1" /> : <AlertCircle className="w-6 h-6 text-amber-700 shrink-0 mt-1" />}
          <div className="text-sm">
            <div className={`font-black ${data.twilio_configured ? "text-emerald-800" : "text-amber-800"}`}>
              {data.twilio_configured ? "Twilio مفعّل — الإرسال الفعلي يعمل" : "Twilio غير مفعّل حالياً"}
            </div>
            <div className="mt-1 text-slate-700">
              لإرسال هذه القوالب فعلياً، تأكد من ملء المتغيرات في backend/.env:
              <span dir="ltr" className="mx-1 font-mono text-xs bg-white px-2 py-0.5 rounded border">TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_WHATSAPP_FROM</span>
              ثم أضف SID كل قالب بعد اعتماده من Meta:
              <span dir="ltr" className="mx-1 font-mono text-xs bg-white px-2 py-0.5 rounded border">TWILIO_TEMPLATE_MONTHLY_SID / TWILIO_TEMPLATE_SESSION_SID / TWILIO_TEMPLATE_ABSENCE_SID</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {data.templates.map((t) => (
          <Card key={t.kind} data-testid={`tpl-${t.kind}`} className="card-elevated border-slate-200/70">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-black text-slate-900">{t.title}</div>
                    <div className="text-[11px] text-slate-500" dir="ltr">{t.key} · {t.category} · {t.language}</div>
                  </div>
                </div>
                <Badge className={t.configured ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>
                  {t.configured ? "SID مضبوط" : "بانتظار الاعتماد"}
                </Badge>
              </div>

              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 whitespace-pre-wrap text-sm font-medium text-slate-800">
                {t.body}
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-slate-500 mb-2">تعيين المتغيرات</div>
                <ol className="space-y-1 text-sm text-slate-700">
                  {t.variables_hint.map((v, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-slate-100 text-slate-700 font-mono text-xs flex items-center justify-center">{`{{${i + 1}}}`}</span>
                      <span>{v}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <Button
                  data-testid={`copy-body-${t.kind}`}
                  onClick={() => copy(t.body)}
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" /> نسخ نص القالب
                </Button>
                <Button
                  data-testid={`copy-key-${t.kind}`}
                  onClick={() => copy(t.key)}
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" /> نسخ المُعرِّف
                </Button>
                {t.sid && (
                  <span className="text-[11px] text-slate-500 mr-auto font-mono" dir="ltr">SID: {t.sid.slice(0, 10)}…</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200/70">
        <CardContent className="p-6 space-y-3">
          <div className="font-black text-slate-900 text-lg">خطوات الاعتماد على Twilio</div>
          <ol className="list-decimal pr-5 space-y-2 text-sm text-slate-700">
            <li>افتح Twilio Console → Content Editor → Create Template</li>
            <li>اختر Language: Arabic، Category: <b>UTILITY</b>، والصق نص القالب المطابق</li>
            <li>احرص على وضع المتغيرات <code dir="ltr" className="font-mono bg-slate-100 px-1">{`{{1}}, {{2}}...`}</code> بنفس الترتيب أعلاه</li>
            <li>أرسل للمراجعة (submit for approval) — Meta ترد عادة خلال دقائق حتى بضع ساعات</li>
            <li>بعد الاعتماد، انسخ SID القالب (<code dir="ltr" className="font-mono bg-slate-100 px-1">HX...</code>) والصقه في backend/.env في المتغير المناسب</li>
            <li>أعد تشغيل الـ backend — سيرسل النظام بالقالب المعتمد تلقائياً</li>
          </ol>
          <a
            href="https://console.twilio.com/us1/develop/sms/content-template-builder"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sky-700 font-bold hover:underline text-sm"
          >
            <ExternalLink className="w-4 h-4" /> افتح Twilio Content Editor
          </a>
        </CardContent>
      </Card>
    </div>
  );
}
